export const PLAN_EXPIRY_POPUP_SESSION_KEY = 'purchasePlanExpiryPopup';
export const PLAN_EXPIRY_REMINDER_DAYS = 5;

const DAY_MS = 24 * 60 * 60 * 1000;
const IST_DATE = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Kolkata',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit'
});

export function markPlanExpiryPopupPending(): void {
  try {
    sessionStorage.setItem(PLAN_EXPIRY_POPUP_SESSION_KEY, '1');
  } catch {
    // Session storage can be unavailable in private browsing.
  }
}

export function hasPlanExpiryPopupPending(): boolean {
  try {
    return sessionStorage.getItem(PLAN_EXPIRY_POPUP_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

export function clearPlanExpiryPopupPending(): void {
  try {
    sessionStorage.removeItem(PLAN_EXPIRY_POPUP_SESSION_KEY);
  } catch {
    // Ignore storage failures; the popup simply will not repeat.
  }
}

function utcDay(year: number, month: number, day: number): number | null {
  if (!year || !month || !day) {
    return null;
  }

  return Date.UTC(year, month - 1, day);
}

/** Calendar day in IST, as a UTC midnight timestamp, so day differences ignore the clock time. */
export function istCalendarDay(value: unknown): number | null {
  if (value == null || value === '') {
    return null;
  }

  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
    const [year, month, day] = value.trim().split('-').map(Number);
    return utcDay(year, month, day);
  }

  const parsed = value instanceof Date ? value : new Date(value as string | number);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  const parts = IST_DATE.formatToParts(parsed);
  const year = Number(parts.find(part => part.type === 'year')?.value);
  const month = Number(parts.find(part => part.type === 'month')?.value);
  const day = Number(parts.find(part => part.type === 'day')?.value);

  return utcDay(year, month, day);
}

/** Days from today (IST) until the plan date. Null when the date is missing or already past. */
export function daysUntilPlanExpiry(subscriptionDate: unknown, now: Date = new Date()): number | null {
  const expiry = istCalendarDay(subscriptionDate);
  const today = istCalendarDay(now);

  if (expiry == null || today == null) {
    return null;
  }

  const days = Math.round((expiry - today) / DAY_MS);
  return days < 0 ? null : days;
}

export function soonestActivePlanDays(dates: unknown[], now: Date = new Date()): number | null {
  let soonest: number | null = null;

  (dates || []).forEach(date => {
    const days = daysUntilPlanExpiry(date, now);
    if (days == null) {
      return;
    }

    if (soonest == null || days < soonest) {
      soonest = days;
    }
  });

  return soonest;
}

export function isPlanNearingExpiry(days: number | null): boolean {
  return days != null && days <= PLAN_EXPIRY_REMINDER_DAYS;
}

export function planExpiryPopupMessage(days: number): string {
  if (days <= 0) {
    return 'Your Purchase by Petpooja plan expires today.';
  }

  const unit = days === 1 ? 'day' : 'days';
  return `Your Purchase by Petpooja plan expires in ${days} ${unit}.`;
}

export function planExpiryDaysLabel(days: number): string {
  if (days <= 0) {
    return 'Expires today';
  }

  if (days === 1) {
    return '1 day left';
  }

  return `${days} days left`;
}

export interface PlanExpiryNotice {
  id: string;
  name: string;
  days: number;
}

/**
 * One line per company inside the reminder window.
 * Outlets of the same company that expire on different days are listed separately.
 */
export function expiringCompanyNotices(
  outlets: Array<{
    id?: unknown;
    name?: unknown;
    group?: unknown;
    parentId?: unknown;
    parent?: unknown;
    subscriptionDate?: unknown;
  }> | null | undefined,
  now: Date = new Date()
): PlanExpiryNotice[] {
  const groups = new Map<string, { companyName: string; items: { id: string; outletName: string; days: number }[] }>();

  (outlets || []).forEach(outlet => {
    if (!outlet || outlet.id == null || outlet.id === '' || outlet.id === '0' || outlet.id === 0) {
      return;
    }

    const days = daysUntilPlanExpiry(outlet.subscriptionDate, now);
    if (!isPlanNearingExpiry(days)) {
      return;
    }

    const companyName = String(outlet.group || outlet.name || 'Company').trim() || 'Company';
    const key = outlet.parentId != null
      ? String(outlet.parentId)
      : (outlet.parent != null ? String(outlet.parent) : companyName);
    const bucket = groups.get(key) || { companyName, items: [] };

    bucket.items.push({
      id: String(outlet.id),
      outletName: String(outlet.name || companyName).trim() || companyName,
      days
    });
    groups.set(key, bucket);
  });

  const notices: PlanExpiryNotice[] = [];

  groups.forEach(bucket => {
    const uniqueDays = new Set(bucket.items.map(item => item.days));

    if (uniqueDays.size === 1) {
      notices.push({
        id: bucket.items.map(item => item.id).join(','),
        name: bucket.companyName,
        days: bucket.items[0].days
      });
      return;
    }

    bucket.items.forEach(item => {
      const name = item.outletName !== bucket.companyName
        ? `${bucket.companyName} — ${item.outletName}`
        : item.outletName;

      notices.push({ id: item.id, name, days: item.days });
    });
  });

  notices.sort((left, right) => left.days - right.days || left.name.localeCompare(right.name));

  return notices;
}

export function subscriptionDatesFromOutlets(
  outlets: Array<{ id?: unknown; subscriptionDate?: unknown }> | null | undefined,
  catalog: Array<{ id?: unknown; subscriptionDate?: unknown }> = []
): unknown[] {
  return (outlets || [])
    .filter(outlet => outlet && outlet.id != null && outlet.id !== '' && outlet.id !== '0' && outlet.id !== 0)
    .map(outlet => {
      const match = (catalog || []).find(item => item?.id === outlet.id);
      return match?.subscriptionDate ?? outlet.subscriptionDate ?? null;
    })
    .filter(date => date != null && date !== '');
}

export function subscriptionDatesFromCompany(
  company: { subscriptionDate?: unknown; outlets?: Array<{ subscriptionDate?: unknown }> } | null | undefined
): unknown[] {
  if (!company) {
    return [];
  }

  if (company.subscriptionDate) {
    return [company.subscriptionDate];
  }

  return (company.outlets || [])
    .map(outlet => outlet?.subscriptionDate)
    .filter(date => date != null && date !== '');
}
